// src/pages/VTuberInvitePage.tsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  visible: {
    transition: { staggerChildren: 0.13 },
  },
};

const VTuberInvitePage: React.FC = () => {
  const { t } = useTranslation('vtuberInvite');
  const { user } = useAuth();

  // 設定完了後に戻ってきた場合、ウィザードを自動で開く
  useEffect(() => {
    const stored = sessionStorage.getItem('openWizardOnLoad');
    if (stored && user) {
      sessionStorage.removeItem('openWizardOnLoad');
      const { defaultCategory } = JSON.parse(stored);
      // 少し遅延させてページのマウントを待つ
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-create-wizard', {
          detail: { defaultCategory: defaultCategory || 'vtuber' },
        }));
      }, 300);
    }
  }, [user]);

  const handleCTA = () => {
    if (user) {
      window.dispatchEvent(new CustomEvent('open-create-wizard', {
        detail: { defaultCategory: 'vtuber' },
      }));
    } else {
      // ログイン後にウィザードが自動で開く
      window.dispatchEvent(new CustomEvent('open-signup-then-create', {
        detail: { defaultCategory: 'vtuber', returnTo: '/vtuber/invite' },
      }));
    }
  };

  const steps = [1, 2, 3, 4] as const;

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors">

      {/* ===== Hero ===== */}
      <section className="relative px-6 pt-24 pb-16 sm:pt-32 sm:pb-24 bg-[#f5f3f8] dark:bg-gray-900">
        <motion.div
          className="mx-auto max-w-3xl text-center"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.p
            className="text-sm font-medium text-[#B566FF] dark:text-purple-400 tracking-wide mb-4"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            {t('hero.label')}
          </motion.p>

          <motion.h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight whitespace-pre-line"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            {t('hero.catchcopy')}
          </motion.h1>

          <motion.p
            className="mt-5 text-base sm:text-lg text-gray-500 dark:text-gray-400 leading-relaxed"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            {t('hero.subcopy')}
          </motion.p>

          <motion.div
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <button
              onClick={handleCTA}
              className="mt-9 px-10 py-3.5 bg-[#B566FF] hover:bg-[#a350f0] text-white text-base font-bold rounded-full shadow-md hover:shadow-lg transition-all duration-200"
            >
              {t('hero.cta')}
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== Campaign ===== */}
      <section className="px-6 py-10 sm:py-14 bg-white dark:bg-gray-800/40">
        <motion.div
          className="mx-auto max-w-4xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          variants={stagger}
        >
          <motion.div
            className="relative rounded-2xl border-2 border-[#B566FF] dark:border-purple-500 bg-purple-50/60 dark:bg-purple-900/20 overflow-hidden"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <div className="flex flex-col md:flex-row items-center">
              {/* テキスト（左側） */}
              <div className="flex-1 px-7 py-8 sm:px-10 sm:py-10">
                <span className="inline-block px-4 py-1 text-xs font-bold text-white bg-[#B566FF] rounded-full mb-4">
                  {t('campaign.label')}
                </span>
                <p className="text-lg sm:text-xl font-bold text-[#B566FF] dark:text-purple-300 mb-1">
                  {t('campaign.highlight')}
                </p>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {t('campaign.title')}
                </h2>
                <p className="mt-3 text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                  {t('campaign.description')}
                </p>
                <button
                  onClick={handleCTA}
                  className="mt-5 px-7 py-2.5 bg-[#B566FF] hover:bg-[#a350f0] text-white text-sm font-bold rounded-full shadow-md hover:shadow-lg transition-all duration-200"
                >
                  {t('campaign.cta')}
                </button>
              </div>
              {/* model-viewer（右側） */}
              <div className="w-full md:w-64 lg:w-72 h-56 md:h-auto md:self-stretch flex-shrink-0">
                <model-viewer
                  src="/glb/preview_premium_acrylic.glb"
                  auto-rotate=""
                  rotation-per-second="20deg"
                  camera-controls=""
                  interaction-prompt="none"
                  camera-orbit="15deg 90deg 100%"
                  field-of-view="35deg"
                  min-field-of-view="35deg"
                  max-field-of-view="40deg"
                  disable-pan=""
                  disable-zoom=""
                  environment-image=""
                  exposure="0.8"
                  shadow-intensity="0"
                  tone-mapping="neutral"
                  environment-intensity="0.3"
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '200px',
                    backgroundColor: 'transparent',
                    '--poster-color': 'transparent',
                  } as React.CSSProperties}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== Features ===== */}
      <section className="px-6 py-16 sm:py-20 bg-[#f5f3f8] dark:bg-gray-900">
        <motion.div
          className="mx-auto max-w-5xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-center text-gray-900 dark:text-white"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            {t('features.title')}
          </motion.h2>

          {/* Profile — メインカード */}
          <motion.div
            className="mt-10 bg-[#f9f8fb] dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 p-7 md:p-10">
              {/* カードモックアップ */}
              <div className="w-full md:w-1/2 flex-shrink-0">
                <div className="relative aspect-[4/3] bg-white dark:bg-gray-700 rounded-xl shadow-md overflow-hidden border border-gray-100 dark:border-gray-600">
                  {/* カードUI風のモック */}
                  <div className="absolute inset-0 flex flex-col">
                    {/* ヘッダー部分 */}
                    <div className="h-1/3 bg-gradient-to-r from-purple-200 to-pink-100 dark:from-purple-900/60 dark:to-pink-900/40" />
                    {/* コンテンツ部分 */}
                    <div className="flex-1 px-5 pt-4 pb-3">
                      {/* アバター */}
                      <div className="w-12 h-12 rounded-full bg-purple-300 dark:bg-purple-600 border-2 border-white dark:border-gray-700 -mt-10 shadow-sm" />
                      {/* 名前 */}
                      <div className="mt-3 h-3 w-24 bg-gray-300 dark:bg-gray-500 rounded-full" />
                      {/* タグ */}
                      <div className="mt-2.5 flex gap-2">
                        <div className="h-2.5 w-14 bg-purple-200 dark:bg-purple-800/60 rounded-full" />
                        <div className="h-2.5 w-10 bg-blue-200 dark:bg-blue-800/60 rounded-full" />
                      </div>
                      {/* テキスト行 */}
                      <div className="mt-4 space-y-2">
                        <div className="h-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-2 w-4/5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-2 w-3/5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                      </div>
                      {/* SNSアイコン行 */}
                      <div className="mt-4 flex gap-2">
                        <div className="w-6 h-6 rounded-md bg-gray-200 dark:bg-gray-600" />
                        <div className="w-6 h-6 rounded-md bg-gray-200 dark:bg-gray-600" />
                        <div className="w-6 h-6 rounded-md bg-gray-200 dark:bg-gray-600" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* テキスト */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {t('features.profile.title')}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                  {t('features.profile.description')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Search & Protect — サブカード2列 */}
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {/* Search */}
            <motion.div
              className="bg-[#f9f8fb] dark:bg-gray-800 rounded-2xl p-7 shadow-sm"
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {t('features.search.title')}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {t('features.search.description')}
              </p>
            </motion.div>

            {/* Protect */}
            <motion.div
              className="bg-[#f9f8fb] dark:bg-gray-800 rounded-2xl p-7 shadow-sm"
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
            >
              <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/40 flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {t('features.protect.title')}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {t('features.protect.description')}
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ===== Steps ===== */}
      <section className="px-6 py-16 sm:py-20 bg-white dark:bg-gray-800/40">
        <motion.div
          className="mx-auto max-w-4xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.h2
            className="text-2xl sm:text-3xl font-bold text-center text-gray-900 dark:text-white"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            {t('steps.title')}
          </motion.h2>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step) => (
              <motion.div
                key={step}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center shadow-sm"
                variants={fadeInUp}
                transition={{ duration: 0.4 }}
              >
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#B566FF] text-white text-sm font-bold mb-4">
                  {step}
                </span>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {t(`steps.step${step}.title`)}
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t(`steps.step${step}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ===== CTA ===== */}
      <section className="px-6 py-16 sm:py-24 bg-white dark:bg-gray-800/40">
        <motion.div
          className="mx-auto max-w-2xl text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.span
            className="inline-block px-5 py-2 text-sm font-medium text-[#B566FF] dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 rounded-full"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            {t('cta.badge')}
          </motion.span>

          <motion.h2
            className="mt-6 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            {t('cta.title')}
          </motion.h2>

          <motion.div
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <button
              onClick={handleCTA}
              className="mt-8 px-10 py-3.5 bg-[#B566FF] hover:bg-[#a350f0] text-white text-base font-bold rounded-full shadow-md hover:shadow-lg transition-all duration-200"
            >
              {t('cta.button')}
            </button>
          </motion.div>

          <motion.p
            className="mt-4 text-xs text-gray-400 dark:text-gray-500"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            {t('cta.note')}
          </motion.p>
        </motion.div>
      </section>
    </div>
  );
};

export default VTuberInvitePage;
